import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getApiErrorInfo,
  getPublicProject,
  postProjectView,
  postProjectContactClick,
  postSimilarRequest,
  type ApiErrorInfo,
  type OwnerContact,
  type PublicProject,
} from '../lib/api';
import { useBackButton } from '../hooks/useBackButton';
import { isLocalAppEnv } from '../lib/runtimeEnv';
import { ImagePlaceholderIcon } from './icons';

const SUBTITLE_MAX_LEN = 120;

/**
 * Умное обрезание подзаголовка: trim, замена переносов на пробел, обрезка по последнему пробелу.
 * Fallback на title, если problem пустой. Без обрезки слова, без "…" после пробела, без двойных пробелов.
 */
export function smartSubtitle(
  problem: string | null | undefined,
  title: string | null | undefined,
  maxLen: number = SUBTITLE_MAX_LEN
): string {
  const normalized = (problem || '')
    .trim()
    .replace(/\s+/g, ' ')
    .trim();
  const source = normalized || (title || '').trim() || '';
  if (!source) return '';
  if (source.length <= maxLen) return source;
  const slice = source.slice(0, maxLen);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;
  return cut + '…';
}

const WHAT_DONE_PREFIX = /^[-•]\s*|^\d+[.)]\s*/;

/**
 * Нормализация списка «Что сделано»: splitlines, trim, удаление пустых и префиксов "- ", "• ", "1. ", "1) ".
 */
export function normalizeWhatDoneLines(text: string | null | undefined): string[] {
  if (!text || !String(text).trim()) return [];
  return text
    .split(/\r\n|\n|\r/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.replace(WHAT_DONE_PREFIX, '').trim())
    .filter(Boolean);
}

function goalLabel(value: string | null | undefined): string | null {
  const v = String(value || '').trim();
  if (!v) return null;
  switch (v) {
    case 'sale':
      return 'Продажа';
    case 'investment':
      return 'Инвестиции';
    case 'partnership':
      return 'Партнерство';
    case 'team':
      return 'Поиск команды';
    case 'feedback':
      return 'Обратная связь';
    default:
      return v;
  }
}

function PublicError({
  info,
  message,
  onRetry,
}: {
  info: ApiErrorInfo | null;
  message: string | null;
  onRetry?: () => void;
}) {
  if (!info && !message) return null;
  const req = info?.request ?? null;
  const hasDiagnostics = isLocalAppEnv() && (!!req || !!info?.errorReportRequestId);
  return (
    <div className="error">
      <p>{message || 'Не удалось загрузить проект'}</p>
      {onRetry ? (
        <button className="btn btn-secondary" onClick={onRetry}>
          Повторить
        </button>
      ) : null}
      {hasDiagnostics ? (
        <details className="diagnostics-panel">
          <summary>Подробности</summary>
          <div className="diagnostics-content">
            {req ? (
              <p className="error-meta">
                <span className="diagnostics-key">request</span>{' '}
                <span className="diagnostics-value">
                  {req.method} {req.url} status={info?.status ?? '-'}
                </span>
              </p>
            ) : null}
            {info?.errorReportRequestId ? (
              <p className="error-meta">
                <span className="diagnostics-key">error_report_id</span>{' '}
                <span className="diagnostics-value">{info.errorReportRequestId}</span>
              </p>
            ) : null}
          </div>
        </details>
      ) : null}
    </div>
  );
}

function Section({ title, value }: { title: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="detail-section">
      <h3 className="detail-section-title">{title}</h3>
      <div className="detail-preview-html" style={{ maxHeight: 'unset' }}>
        {value}
      </div>
    </div>
  );
}

function ContactModal({
  contact,
  onClose,
  onCopyId,
}: {
  contact: OwnerContact;
  onClose: () => void;
  onCopyId: (id: number) => void;
}) {
  const hasUsername = Boolean(contact.username && String(contact.username).trim());
  const displayName = contact.first_name || contact.username || `ID ${contact.telegram_id}`;
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3 className="detail-section-title">Контакт автора</h3>
        <p style={{ marginBottom: 8 }}>
          <strong>{displayName}</strong>
        </p>
        {contact.username ? (
          <p style={{ marginBottom: 8, color: 'var(--text-muted)' }}>@{contact.username}</p>
        ) : null}
        <p style={{ marginBottom: 12, fontSize: 14, color: 'var(--text-muted)' }}>
          Telegram ID: {contact.telegram_id}
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {hasUsername ? (
            <a
              href={`https://t.me/${contact.username!.replace(/^@/, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Открыть в Telegram
            </a>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onCopyId(contact.telegram_id)}
            >
              Скопировать Telegram ID
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}

function SimilarRequestModal({
  onClose,
  onSubmit,
  loading,
  error,
}: {
  onClose: () => void;
  onSubmit: (payload: { message: string; budget: string; deadline: string; contact_preference: string }) => void;
  loading: boolean;
  error: string | null;
}) {
  const [message, setMessage] = useState('');
  const [budget, setBudget] = useState('');
  const [deadline, setDeadline] = useState('');
  const [contactPreference, setContactPreference] = useState('telegram');
  const canSubmit = message.trim().length >= 20 && !loading;
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <h3 className="detail-section-title">Заказать похожее</h3>
        <p style={{ marginBottom: 12, fontSize: 14, color: 'var(--text-muted)' }}>
          Опишите, что нужно сделать. Автор проекта получит заявку и сможет связаться с вами.
        </p>
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="similar-message" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            Что нужно сделать? <span style={{ color: 'var(--text-muted)' }}>(не менее 20 символов)</span>
          </label>
          <textarea
            id="similar-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Например: нужен похожий лендинг под мой продукт..."
            rows={4}
            className="input"
            style={{ width: '100%', resize: 'vertical', minHeight: 80 }}
            maxLength={5000}
            disabled={loading}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="similar-budget" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            Бюджет <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(необязательно)</span>
          </label>
          <input
            id="similar-budget"
            type="text"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="до 2000$"
            className="input"
            style={{ width: '100%' }}
            maxLength={500}
            disabled={loading}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="similar-deadline" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            Срок <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(необязательно)</span>
          </label>
          <input
            id="similar-deadline"
            type="text"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            placeholder="2 недели"
            className="input"
            style={{ width: '100%' }}
            maxLength={500}
            disabled={loading}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="similar-contact" style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            Как связаться
          </label>
          <select
            id="similar-contact"
            value={contactPreference}
            onChange={(e) => setContactPreference(e.target.value)}
            className="input"
            style={{ width: '100%' }}
            disabled={loading}
          >
            <option value="telegram">Telegram</option>
            <option value="email">Email</option>
            <option value="phone">Телефон</option>
          </select>
        </div>
        {error ? (
          <p className="error" style={{ marginBottom: 12 }}>
            {error}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                message: message.trim(),
                budget: budget.trim() || '',
                deadline: deadline.trim() || '',
                contact_preference: contactPreference,
              })
            }
          >
            {loading ? 'Отправка…' : 'Отправить заявку'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

export function PublicProjectScreen({
  idOrSlug,
  onBack,
}: {
  idOrSlug: string;
  onBack?: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PublicProject | null>(null);
  const [errorInfo, setErrorInfo] = useState<ApiErrorInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactModal, setContactModal] = useState<OwnerContact | null>(null);
  const [contactButtonHidden, setContactButtonHidden] = useState(false);
  const [similarModalOpen, setSimilarModalOpen] = useState(false);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarError, setSimilarError] = useState<string | null>(null);
  const [similarSuccess, setSimilarSuccess] = useState(false);

  useBackButton(onBack ?? (() => {}), Boolean(onBack));

  const load = useCallback(async () => {
    setLoading(true);
    setErrorInfo(null);
    setErrorMessage(null);
    try {
      const project = await getPublicProject(idOrSlug);
      setData(project);
      postProjectView(idOrSlug).catch(() => {});
    } catch (err) {
      const info = getApiErrorInfo(err);
      setErrorInfo(info);
      setErrorMessage(info.message || 'Не удалось загрузить проект');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [idOrSlug]);

  const handleContactClick = useCallback(async () => {
    if (!data?.id) return;
    setContactLoading(true);
    setContactError(null);
    try {
      const res = await postProjectContactClick(data.id);
      setContactModal(res.owner_contact);
    } catch (err) {
      const info = getApiErrorInfo(err);
      if (info.status === 401) {
        setContactError('Требуется авторизация');
      } else if (info.status === 400) {
        setContactButtonHidden(true);
      } else if (info.status && info.status >= 500) {
        setContactError('Ошибка соединения');
      } else {
        setContactError(info.message || 'Ошибка');
      }
    } finally {
      setContactLoading(false);
    }
  }, [data?.id]);

  const handleCopyTelegramId = useCallback((telegramId: number) => {
    const text = String(telegramId);
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => setContactModal(null));
    } else {
      setContactModal(null);
    }
  }, []);

  const handleSimilarSubmit = useCallback(
    async (payload: { message: string; budget: string; deadline: string; contact_preference: string }) => {
      if (!data?.id) return;
      setSimilarLoading(true);
      setSimilarError(null);
      try {
        await postSimilarRequest(data.id, {
          message: payload.message,
          budget: payload.budget || undefined,
          deadline: payload.deadline || undefined,
          contact_preference: payload.contact_preference || undefined,
        });
        setSimilarSuccess(true);
        setSimilarModalOpen(false);
      } catch (err) {
        const info = getApiErrorInfo(err);
        if (info.status === 401) {
          setSimilarError('Требуется авторизация');
        } else if (info.status === 400) {
          const msg = (info.message || '').toLowerCase();
          if (msg.includes('own') || msg.includes('self') || msg.includes('сво')) {
            setSimilarError('Нельзя отправить заявку себе');
          } else {
            setSimilarError('Опишите задачу подробнее (минимум 20 символов)');
          }
        } else if (info.status === 404) {
          setSimilarError('Проект недоступен');
        } else if (info.status === 429) {
          setSimilarError('Вы уже отправляли заявку недавно. Попробуйте позже.');
        } else if (info.status && info.status >= 500) {
          setSimilarError('Ошибка соединения');
        } else {
          setSimilarError(info.message || 'Ошибка');
        }
      } finally {
        setSimilarLoading(false);
      }
    },
    [data?.id]
  );

  useEffect(() => {
    load();
  }, [load]);

  const publishedAt = useMemo(() => {
    if (!data?.published_at) return null;
    try {
      return new Date(data.published_at).toLocaleString('ru');
    } catch {
      return data.published_at;
    }
  }, [data?.published_at]);

  if (loading) {
    return (
      <div className="detail-screen">
        <p>Загрузка...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="detail-screen">
        <PublicError info={errorInfo} message={errorMessage} onRetry={load} />
      </div>
    );
  }

  const goal = goalLabel(data.goal);
  const coverUrl = data.cover_image_url?.trim();
  const subtitle = smartSubtitle(data.problem, data.title, SUBTITLE_MAX_LEN);
  const showCta = data.published_at && data.is_owner !== true;
  const whatDoneItems = normalizeWhatDoneLines(data.what_done);

  const ctaBlock = showCta ? (
    <div className="public-project-cta">
      {contactButtonHidden ? (
        <span className="public-project-owner-hint" title="Кнопка «Связаться» недоступна для автора проекта">
          Это ваш проект
        </span>
      ) : (
        <button
          type="button"
          className="btn btn-primary btn-cta"
          onClick={handleContactClick}
          disabled={contactLoading}
        >
          {contactLoading ? 'Загрузка…' : 'Связаться'}
        </button>
      )}
      <button
        type="button"
        className="btn btn-secondary btn-cta"
        onClick={() => {
          setSimilarError(null);
          setSimilarModalOpen(true);
        }}
      >
        Заказать похожее
      </button>
      {contactError ? (
        <p className="error" style={{ marginTop: 8, width: '100%' }}>
          {contactError}
        </p>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="detail-screen">
      <div className="detail-card public-project-card">
        {/* A. Hero: обложка 16:9 full width, заголовок, подзаголовок, CTA */}
        <div className="public-project-hero">
          <div className="public-project-hero-cover">
            {coverUrl ? (
              <img src={coverUrl} alt="" />
            ) : (
              <div className="public-project-hero-placeholder">
                <span className="cover-placeholder-emoji" aria-hidden><ImagePlaceholderIcon size={40} /></span>
                <span>Без обложки</span>
              </div>
            )}
          </div>
          <div className="public-project-hero-content">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
              <h1 className="public-project-title">{data.title || 'Без названия'}</h1>
              <span className="badge badge-approved">Опубликовано</span>
            </div>
            {publishedAt ? (
              <p className="progress-text" style={{ marginTop: 4, marginBottom: 0 }}>
                {publishedAt}
              </p>
            ) : null}
            {subtitle ? <p className="public-project-subtitle">{subtitle}</p> : null}
            {showCta ? ctaBlock : null}
          </div>
        </div>

        <div className="public-project-body">
          {/* B. О проекте */}
          {(data.problem || '').trim() ? (
            <section className="public-project-block public-project-about">
              <h2 className="public-project-block-title">О проекте</h2>
              <div className="public-project-readable">{(data.problem ?? '').trim()}</div>
            </section>
          ) : null}

          {/* C. Что сделано */}
          {whatDoneItems.length > 0 ? (
            <section className="public-project-block">
              <h2 className="public-project-block-title">Что сделано</h2>
              {whatDoneItems.length > 1 ? (
                <ul className="public-project-list">
                  {whatDoneItems.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : (
                <div className="public-project-readable">{whatDoneItems[0]}</div>
              )}
            </section>
          ) : null}

          {/* D. Результат */}
          {(data.potential || '').trim() ? (
            <section className="public-project-block public-project-result">
              <h2 className="public-project-block-title">Результат</h2>
              <div className="public-project-result-inner">{data.potential!.trim()}</div>
            </section>
          ) : null}

          {/* Доп. детали для старых проектов */}
          {(data.audience_type || data.niche || data.stack || data.dev_time || goal) ? (
            <section className="public-project-block public-project-details">
              {data.audience_type ? <Section title="Для кого" value={data.audience_type} /> : null}
              {data.niche ? <Section title="Ниша" value={data.niche} /> : null}
              {data.stack ? <Section title="Стек / инструменты" value={data.stack} /> : null}
              {data.dev_time ? <Section title="Срок разработки" value={data.dev_time} /> : null}
              {goal ? <Section title="Цель" value={goal} /> : null}
            </section>
          ) : null}

          {data.show_contacts && data.contact_value ? (
            <div className="detail-section">
              <h3 className="detail-section-title">Контакты</h3>
              <div className="detail-preview-html" style={{ maxHeight: 'unset' }}>
                {data.author_name ? (
                  <div style={{ marginBottom: 8 }}>
                    <strong>{data.author_name}</strong>
                  </div>
                ) : null}
                <div>
                  {data.contact_mode ? <span style={{ color: 'var(--text-muted)' }}>{data.contact_mode}: </span> : null}
                  {data.contact_value}
                </div>
              </div>
            </div>
          ) : null}

          {/* E. Нижний CTA */}
          {showCta ? (
            <section className="public-project-cta-bottom">
              {ctaBlock}
            </section>
          ) : null}

          <div className="detail-meta">
            <div className="detail-meta-row">
              <span className="detail-meta-key">ID</span>
              <span className="detail-meta-value">{data.id.slice(0, 8)}…</span>
            </div>
            <div className="detail-meta-row">
              <span className="detail-meta-key">Ссылка</span>
              <span className="detail-meta-value">{data.public_url}</span>
            </div>
          </div>
        </div>
      </div>

      {similarSuccess ? (
        <p className="progress-text" style={{ marginTop: 8, color: 'var(--color-success, green)' }}>
          Заявка отправлена
        </p>
      ) : null}

      {similarModalOpen ? (
        <SimilarRequestModal
          onClose={() => {
            setSimilarModalOpen(false);
            setSimilarError(null);
          }}
          onSubmit={handleSimilarSubmit}
          loading={similarLoading}
          error={similarError}
        />
      ) : null}

      {contactModal ? (
        <ContactModal
          contact={contactModal}
          onClose={() => setContactModal(null)}
          onCopyId={handleCopyTelegramId}
        />
      ) : null}
    </div>
  );
}
